import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import debounce from 'lodash/debounce';
import { ui } from '@/constants';
import { PointsActivity } from '@/models';

import { PointEventType } from './CmgPointsHistoryCell/cmg-points-history-cell';
import { inViewport } from '@/helpers';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { ButtonType } from '@/components/CmgButton/cmg-button';

@Component({
  name: 'CmgPointsHistory',
})
export default class CmgPointsHistory extends Vue {
  isLoading = false;
  errorState = false;
  moreHistoryAvailable = true;
  pointsHistoryWithDividers: PointsActivity[] = [];
  private debouncedOnScroll = debounce(this.onScroll, 200);

  get pointsHistory() {
    this.pointsHistoryWithDividers = [];

    if (this.hasPointsHistory) {
      // get the first month and add the initial divider
      const lastEventActivityDate = this.lastEvent!.activityDate;
      let lastEventMonth = lastEventActivityDate.getMonth();
      let lastEventYear = lastEventActivityDate.getFullYear();

      this.addDividerCell(lastEventActivityDate);

      // look for each of the month boundaries and add the divider
      this.$appStore.state.customer.loyalty.pointsHistory.forEach(event => {
        const decrementMonth = () => {
          lastEventMonth--;

          if (lastEventMonth === -1) {
            lastEventMonth = 11;
            lastEventYear--;
          }
        };

        const eventYear = event.activityDate.getFullYear();
        const eventMonth = event.activityDate.getMonth();

        // If it is a new month of activity
        if (eventYear !== lastEventYear || eventMonth !== lastEventMonth) {
          // Condense months with of inactivity into a single divider and single no activity cell
          decrementMonth();
          const inactivityStartDate = new Date(lastEventYear, lastEventMonth);
          const inactivityStartMonth = inactivityStartDate.getMonth();
          const inactivityStartYear = inactivityStartDate.getFullYear();

          let inactivityEndDate = new Date(lastEventYear, lastEventMonth);

          // Find the end of the no activity period
          while (eventYear !== lastEventYear || lastEventMonth !== eventMonth) {
            inactivityEndDate = new Date(lastEventYear, lastEventMonth);
            decrementMonth();
          }

          const inactivityEndMonth = inactivityEndDate.getMonth();
          const inactivityEndYear = inactivityEndDate.getFullYear();

          // If the period of no activity is greater than a month
          if (inactivityStartMonth !== inactivityEndMonth || inactivityStartYear !== inactivityEndYear) {
            this.addDividerCell(inactivityStartDate, inactivityEndDate);
            this.addNoActivityCell();
          }

          this.addDividerCell(event.activityDate);

          lastEventMonth = event.activityDate.getMonth();
          lastEventYear = event.activityDate.getFullYear();
        }

        this.pointsHistoryWithDividers.push(event);
      });
    }

    return this.pointsHistoryWithDividers;
  }

  get lastEvent(): PointsActivity | undefined {
    if (this.hasPointsHistory) {
      return this.$appStore.state.customer.loyalty.pointsHistory[0];
    }
  }

  get pepperBadge(): string {
    return this.$appStore.state.cms.images.logos.medallionRewardShout.default;
  }

  get isCustomerStudent(): boolean {
    return this.$appStore.getters.isActiveStudent;
  }

  get isPointsExpiringSoon(): boolean {
    return this.daysTillPointsExpire > 0 && this.daysTillPointsExpire <= 90;
  }

  get isPointsExpired(): boolean {
    return this.daysPointsExpired > 0 && (this.lastEvent?.activityType !== PointEventType.PointsEarned ?? false);
  }

  get daysTillPointsExpire(): number {
    return this.$appStore.state.customer.loyalty.daysTillPointsExpire;
  }

  get daysPointsExpired(): number {
    return this.$appStore.state.customer.loyalty.daysPointsExpired;
  }

  get expirationMessage(): string {
    if (this.isPointsExpiringSoon) {
      return `Your points will expire in ${this.daysTillPointsExpire} day${this.daysTillPointsExpire > 1 ? 's' : ''}.`;
    }

    if (this.isPointsExpired) {
      return `Your points expired ${this.daysPointsExpired} day${this.daysPointsExpired > 1 ? 's' : ''} ago.`;
    }

    return '';
  }

  get expirationSuggestionPath(): string {
    if (this.isPointsExpiringSoon) {
      return 'pages.pointsHistory.expirationSuggestion.pointsExpiringSoon';
    }

    if (this.isPointsExpired) {
      return 'pages.pointsHistory.expirationSuggestion.pointsExpired';
    }

    return '';
  }

  get noPointsTermsPath(): string {
     return 'pages.pointsHistory.noPointsTerms';
  }

  get errorMessage(): string {
    return 'We were unable to retrieve your points history.';
  }

  get points(): number {
    return this.$services.reward.getPointsBalance();
  }

  get noHistoryDescriptionPath(): string {
    return this.points
      ? 'pages.pointsHistory.noHistoryDescription.hasPoints'
      : 'pages.pointsHistory.noHistoryDescription.noPoints'
  }

  get noHistoryTitle(): string {
    return this.points > 0
      ? 'PREVIOUS HISTORY'
      : 'START EARNING POINTS';
  }

  get hasPointsHistory(): boolean {
    return this.$appStore.state.customer.loyalty.pointsHistory.length > 0;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }

  async mounted() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardPointHistory,
      action: AnalyticsEventAction.PageViewed,
      label: AnalyticsEventLabel.RewardPointHistoryPageViewed,
    });
    this.$services.loyalty.clearLoyaltyHistory();
    await this.$services.customer.waitForCustomerInitialization();

    window.addEventListener('scroll', this.debouncedOnScroll);
    const appContent = document.getElementById(ui.componentIds.cmgAppContent);
    if (appContent) {
      appContent.addEventListener('scroll', this.debouncedOnScroll);
      appContent.addEventListener('touchend', this.debouncedOnScroll);
    }

    if (this.pointsHistory.length === 0 && this.isRewardsOnline) {
      await this.getInitialPoints();

      if (this.isTriggerVisible()) {
        this.getMorePoints();
      }
    }
  }

  destroyed() {
    window.removeEventListener('scroll', this.debouncedOnScroll);
    const appContent = document.getElementById(ui.componentIds.cmgAppContent);
    if (appContent) {
      appContent.removeEventListener('scroll', this.debouncedOnScroll);
      appContent.removeEventListener('touchend', this.debouncedOnScroll);
    }
  }

  async getInitialPoints() {
    this.isLoading = true;

    await this.$services.loyalty
      .getLoyaltyHistory(20)
      .then(() => {
        this.errorState = false;
        this.isLoading = false;
        this.moreHistoryAvailable = this.pointsHistory.length >= 20;
      })
      .catch(e => {
        this.handleError(e);
        setTimeout(() => this.isLoading = false, 1000);
      });
  }

  async getMorePoints() {
    if (this.isLoading || !this.moreHistoryAvailable) {
      if(!this.moreHistoryAvailable) {
        window.removeEventListener('scroll', this.debouncedOnScroll);
      }
      return;
    }

    if (this.hasPointsHistory) {
      this.isLoading = true;

      const pointsHistory = this.pointsHistory;
      const earliestEvent = pointsHistory[pointsHistory.length - 1];
      const { activityDate, activityStreamId } = earliestEvent;

      this.moreHistoryAvailable = await this.$services.loyalty
        .getLoyaltyHistory(20, activityDate, activityStreamId)
        .catch(e => this.handleError(e))
        .finally(() => (this.isLoading = false));
    }
  }

  isTriggerVisible(): boolean {
    return inViewport(this.$refs.loadingTrigger as Element);
  }

  addDividerCell(activityDate: Date, endDate?: Date) {
    this.pointsHistoryWithDividers.push({
      activityType: PointEventType.Divider,
      activityDate,
      endDate,
    });
  }

  addNoActivityCell() {
    this.pointsHistoryWithDividers.push({
      activityType: PointEventType.NoActivity,
      activityDate: new Date(),
    });
  }

  handleError(e: Error): boolean {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardPointHistory,
      action: this.errorMessage + AnalyticsEventAction.ErrorDisplayed,
      label: AnalyticsEventLabel.RewardPointHistoryErrorMessage + this.errorMessage,
    });
    this.errorState = true;
    this.$services.logger.error('Something went wrong retrieving points history: ', undefined, e);
    return false;
  }

  onScroll() {
    if (this.$refs.pointsHistoryContainer && this.isRewardsOnline && this.isTriggerVisible()) {
      this.getMorePoints();
    }
  }

  openHistoryModal(event: any) {
    this.$services.ui.setModalVisibility('basicError', true, {
      header: this.$t('pages.pointsHistory.noHistoryTitle.hasPoints') as string,
      content: this.$t('pages.pointsHistory.noHistoryDescription.hasPoints') as string,        
      options: [
        {
          text: 'Okay',
          type: ButtonType.Primary,
          onClick: () => this.$services.ui.setModalVisibility('basicError', false),
        },
      ],
    });
  }
}
